import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Directive, ElementRef, inject, input } from '@angular/core';
import type { InputSignal } from '@angular/core';
import type { ConnectedPosition } from '@angular/cdk/overlay';

export type OverlayOffset = 'left' | 'right';

export type PositionX = 'start' | 'center' | 'end';

export type PositionY = 'top' | 'center' | 'bottom';

export interface Position {
  positionX?: PositionX;
  positionY?: PositionY;
  overlayOffset?: OverlayOffset;
}

@Directive({
  selector: '[tdWbOverlayOrigin]'
})
export class OverlayOriginDirective extends CdkOverlayOrigin {
  private static readonly DEFAULT_POSITION: string = 'center-right';

  override readonly elementRef = inject(ElementRef);

  readonly overlayPosition: InputSignal<string | Array<string> | undefined> = input<string | Array<string> | undefined>(undefined);

  protected readonly DEFAULT_OFFSET: number = 10;

  protected getPositions(): Array<ConnectedPosition> {
    const connectedPositions: Array<ConnectedPosition> = [];
    const positions: Array<Position> = [];

    const overlayPositionValue = this.overlayPosition();
    if (overlayPositionValue !== undefined) {
      // Check if more than one position is set
      if (Array.isArray(overlayPositionValue)) {
        overlayPositionValue.forEach((overlayPosition: string): void => {
          positions.push(this.parsePositions(overlayPosition));
        });
      } else {
        positions.push(this.parsePositions(overlayPositionValue));
      }
    } else {
      positions.push(this.parsePositions(OverlayOriginDirective.DEFAULT_POSITION));
    }

    // Check for valid position and add to connect array
    positions.forEach((position: Position): void => {
      if (this.isValidPositionSpecified(position)) {
        connectedPositions.push(this.getConnectedPosition(position));
      }
    });

    // Default fallback position
    if (connectedPositions.length > 0) {
      connectedPositions.push(this.getFallbackPosition(positions[0]));
      return connectedPositions;
    } else {
      return undefined;
    }
  }

  protected parsePositions(positions: string): Position {
    const position: Position = {};
    const [firstPositionToken, secondPositionToken]: Array<string> = positions.split('-');
    // vertical-offset case
    if (secondPositionToken === 'left') {
      position.positionX = 'end';
      position.positionY = firstPositionToken as PositionY;
      position.overlayOffset = secondPositionToken;
    } else if (secondPositionToken === 'right') {
      position.positionX = 'start';
      position.positionY = firstPositionToken as PositionY;
      position.overlayOffset = secondPositionToken;
    } else if (this.isValidPositionY(secondPositionToken as PositionY) &&
      this.isValidPositionX(firstPositionToken as PositionX)) {
      position.positionY = secondPositionToken as PositionY;
      position.positionX = firstPositionToken as PositionX;
    }
    return position;
  }

  protected getConnectedPosition(position: Position): ConnectedPosition {
    return {
      originX: position.positionX,
      originY: position.positionY,
      overlayX: position.positionX,
      overlayY: this.getOverlayPositionY(position),
      offsetX: this.getOffsetX(position),
      offsetY: this.getOffsetY(position)
    };
  }

  private getFallbackPosition(position: Position): ConnectedPosition {
    return {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetX: this.getOffsetX(position),
      offsetY: this.getOffsetY(position)
    };
  }

  private isValidPositionSpecified(position: Position): boolean {
    return this.isValidPositionX(position.positionX) &&
      this.isValidPositionY(position.positionY) &&
      this.isValidOffset(position.overlayOffset);
  }

  private isValidPositionX(positionX: PositionX): boolean {
    return positionX === 'center' || positionX === 'start' || positionX === 'end';
  }

  private isValidPositionY(positionY: PositionY): boolean {
    return positionY === 'bottom' || positionY === 'center' || positionY === 'top';
  }

  private isValidOffset(overlayOffset: OverlayOffset): boolean {
    return overlayOffset === 'right' || overlayOffset === 'left' || overlayOffset === undefined;
  }

  private getOverlayPositionY(position: Position): PositionY {
    let overlayPositionY: PositionY = 'center';
    if (position.positionY === 'bottom') {
      overlayPositionY = 'top';
    } else if (position.positionY === 'top') {
      overlayPositionY = 'bottom';
    }
    return overlayPositionY;
  }

  private getOffsetY(position: Position): number {
    let offsetY = 0;
    if (position.positionY === 'bottom') {
      offsetY = this.DEFAULT_OFFSET;
    } else if (position.positionY === 'top') {
      offsetY = -this.DEFAULT_OFFSET;
    }
    return offsetY;
  }

  private getOffsetX(position: Position): number {
    let overlayOffset = 0;
    const toggleElementWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
    if (position.overlayOffset === 'left') {
      overlayOffset = -(this.DEFAULT_OFFSET + toggleElementWidth);
    } else if (position.overlayOffset === 'right') {
      overlayOffset = this.DEFAULT_OFFSET + toggleElementWidth;
    }
    return Math.ceil(overlayOffset);
  }

}
